import * as Sentry from '@sentry/react';
import { IsolateMain, Prose, Pushbutton } from '@stratumn/atomic';
import to from 'await-to-js';
import {
  Box,
  ChangePassword,
  GenerateKey,
  Logo,
  SignupStepper
} from 'components/ui';
import { ROUTE_EDIT_PROFILE_SIGNUP, ROUTE_MY_PROFILE } from 'constant/routes';
import compose from 'lodash.flowright';
import { createUserSigningKey } from 'mutations';
import PropType from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';
import { withRouter } from 'react-router-dom';
import { SpanType, withSpanAsync } from 'tracing';
import { withGqlClient, withUser } from 'wrappers';

import styles from './generateKeys.style';

export class GenerateKeys extends React.PureComponent {
  static propTypes = {
    history: PropType.object.isRequired,
    user: PropType.object.isRequired,
    classes: PropType.object.isRequired,
    environment: PropType.object.isRequired
  };

  state = {
    password: null,
    submitted: false,
    signingKey: null
  };

  // Used to cancel key generation function on unmounting.
  immediateKeyGeneration;

  componentDidMount() {
    const {
      history,
      user: {
        account: { signingKey }
      }
    } = this.props;
    if (signingKey) {
      history.push(ROUTE_MY_PROFILE);
    }
  }

  handleStateChange = state => val => this.setState({ [state]: val });

  handlePasswordChange = this.handleStateChange('password');

  handleSigningKeyChange = this.handleStateChange('signingKey');

  isValid() {
    const { submitted, signingKey } = this.state;

    return !submitted && signingKey;
  }

  onSubmit = () =>
    withSpanAsync('commitUserKey', SpanType.processing, async () => {
      const {
        history,
        environment,
        user: { account, rowId: userRowId }
      } = this.props;
      const { signingKey, password } = this.state;

      this.setState({ submitted: true });

      // Add keys
      const { publicKey, privateKey } = signingKey.export(password);

      const [err] = await to(
        createUserSigningKey(
          environment,
          account.id,
          userRowId,
          publicKey,
          privateKey,
          !!password
        )
      );

      if (err) {
        Sentry.captureException(err);
        this.setState({ submitted: false });
      } else {
        history.push(ROUTE_EDIT_PROFILE_SIGNUP);
      }

      return {};
    });

  render() {
    const { classes } = this.props;
    const { firstName, lastName, password, signingKey } = this.state;

    const isValid = this.isValid();

    return (
      <IsolateMain>
        <SignupStepper step={1} />
        <Logo />
        <div className={classes.signingKey}>
          <GenerateKey
            onSigningKeyGeneration={this.handleSigningKeyChange}
            signingKey={signingKey}
          />
        </div>
        <Box>
          <div className={classes.passwordPrompt}>
            <Prose
              light
              text="You can secure your key with an additional password. This password can be the same as your log in password and will be requested every time you submit data in the workflow."
            />
          </div>
          <ChangePassword
            extraExclusionTerms={[firstName, lastName]}
            onChange={this.handlePasswordChange}
          />
          <div className={classes.buttons}>
            <div data-testid="skip-button">
              <Pushbutton disabled={!isValid} onClick={this.onSubmit}>
                Skip this step
              </Pushbutton>
            </div>
            <div data-testid="secure-button">
              <Pushbutton
                disabled={!(isValid && password)}
                onClick={this.onSubmit}
                primary
              >
                Secure Keys
              </Pushbutton>
            </div>
          </div>
        </Box>
      </IsolateMain>
    );
  }
}

export default compose(
  withUser,
  withRouter,
  withGqlClient,
  injectSheet(styles)
)(GenerateKeys);
